import { useTranslation } from "react-i18next";
import { PROJECT_RULE_NAMES, type ProjectRuleName, type ProjectSettings } from "../../../api/settingsApi";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { personName, ruleName } from "../settingsTexts";

/**
 * Who changed each rule last, when and why (accounts.md ST-1), on demand (P-C): the rules still
 * as the installation set them when the project was created share one line.
 */
export default function RuleHistory({ lastChanges }: { lastChanges: ProjectSettings["lastChanges"] }) {
  const { t, i18n } = useTranslation();
  const at = (when: string) => formatDateTime(when, i18n.language);
  const changes = PROJECT_RULE_NAMES.flatMap((name) => {
    const change = lastChanges[name];
    return change === undefined ? [] : [{ name, change }];
  });
  const fromDefaults = new Map<string, ProjectRuleName[]>();
  for (const { name, change } of changes) {
    if (change.reason === "") fromDefaults.set(change.at, [...(fromDefaults.get(change.at) ?? []), name]);
  }
  if (changes.length === 0) return null;
  return (
    <details className="app-text-muted text-xs">
      <summary className="cursor-pointer">{t("projects.settings.rules.history")}</summary>
      <ul className="mt-1 space-y-0.5">
        {changes.filter(({ change }) => change.reason !== "").map(({ name, change }) => (
          <li key={name}>{t("projects.settings.rules.lastChange", { rule: ruleName(t, name), name: personName(t, change.actor), at: at(change.at), reason: change.reason })}</li>
        ))}
        {[...fromDefaults].map(([when, names]) => (
          <li key={when}>{t("projects.settings.rules.fromDefaults", { rules: names.map((name) => ruleName(t, name)).join(", "), at: at(when) })}</li>
        ))}
      </ul>
    </details>
  );
}
